'use client'

import useLogin from '@hook/useLogin'
import { Button, TextareaAutosize } from '@comp/material'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import ControllerFiled from '@comp/form/ControllerFiled'
import StyledInput from '@comp/form/StyledInput'
import StyledCheckbox from '@comp/form/StyledCheckbox'
import { useCallback } from 'react'
import { useAsyncFn, useBoolean } from 'react-use'
import { fetchPost } from '@lib/fetch/client'
import { LoadingButton } from '@mui/lab'
import Image from 'next/image'
import useMessage from '@hook/useMessage'
import { assign, isFunction } from 'lodash-es'
import { commentTypes } from '@lib/config'

const codeIdentify = 'comment'
export default function CommentInputContent({
  contentMinRows = 8,
  newsId,
  topicId,
  replyId,
  commentType,
  onAdd
}) {
  const ts = useTranslations('article')
  const tsc = useTranslations('common')
  const { isLogin, openSigninDialog } = useLogin()

  const form = useForm()
  const body = form.watch('body')
  const captcha = form.watch('captcha')
  const { message } = useMessage()

  // 是否显示图形二维码
  const [showCapcha, toggleShowCapcha] = useBoolean(false)
  // fetch code
  const [codeState, fetchCode] = useAsyncFn(async () => {
    const { src } = await fetchPost(`captcha/${codeIdentify}`)
    return src
  })
  // 显示图形二维码
  const setShowCapcha = useCallback(() => {
    if (showCapcha) return

    toggleShowCapcha(true)
    fetchCode()
  }, [showCapcha, toggleShowCapcha, fetchCode])

  // 验证图形二维码
  const validateCode = useCallback(async (code) => {
    const res = await fetchPost('captcha-check', { identifier: codeIdentify, captcha: code }).catch((err) => {
      form.setError('captcha', err)
      fetchCode()
      return false
    })
    return res
  }, [form, fetchCode])

  // 提交评论
  const [submitState, handleSubmit] = useAsyncFn(async () => {
    if (!await validateCode(form.getValues('captcha'))) return
    const params = form.getValues()
    const postUrl = newsId ? 'news/comment' : 'topic/comment'
    const postParams = assign({
      ...params,
      anonymous: params.anonymous ? 1 : 0,
      replyId
    }, newsId ? { newsId } : { topicId })
    const res = await fetchPost(postUrl, postParams).catch((err) => {
      message(err)
      return false
    })

    if (res) {
      isFunction(onAdd) && onAdd(res)
      message(ts('commentSuccess'))
      fetchCode()
      form.reset()

      if (!isLogin) openSigninDialog()
    }
  }, [isLogin, openSigninDialog])

  return (
    <form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="min-h-[188px] bg-white">
        {
          commentType === commentTypes.loginRequired && !isLogin
            ? (
              <div className="flex justify-center items-center h-pix-188">
                {tsc('needLogin')}
                <Button variant="text" onClick={openSigninDialog}>{tsc('loginNow')}</Button>
              </div>
            )
            : (
              <ControllerFiled
                name="body"
                inputProps={{
                  minRows: contentMinRows,
                  maxRows: 20,
                  className: 'w-full resize-none outline-none p-pix-10 block',
                  placeholder: ts('commentPlaceholder'),
                  onFocus: setShowCapcha,
                }}
                Input={TextareaAutosize}
                form={form}
              />
            )
        }
      </div>
      <div className="flex flex-col md:flex-row items-center mt-pix-10">
        {
          isLogin && (
            <ControllerFiled
              name="anonymous"
              inputProps={{
                label: ts('anonymousComment')
              }}
              Input={StyledCheckbox}
              form={form}
              className="mb-pix-10"
            />
          )
        }
        <div className="ml-auto flex gap-x-pix-8 md:gap-x-pix-10 items-center flex-shrink-0">
          <span>{ts('code')}</span>
          <ControllerFiled
            name="captcha"
            inputProps={{
              placeholder: ts('code'),
              variant: 'outline',
              sx: { padding: '.2rem .8rem' },
              onFocus: setShowCapcha
            }}
            Input={StyledInput}
            form={form}
            rules={{ required: ts('code') }}
            className="w-pix-100 h-pix-40"
          />
          {
            showCapcha && (
              <LoadingButton
                loading={codeState.loading}
                onClick={fetchCode}
                className="!p-0"
              >
                {
                  codeState.value && (
                    <Image
                      width={100}
                      height={40}
                      src={codeState.value || ''}
                      className="w-pix-80 md:w-pix-100 h-pix-36"
                      alt="code"
                    />
                  )
                }
              </LoadingButton>
            )
          }
          <Button type="submit" disabled={submitState.loading || !body || !captcha || (!isLogin && commentType === commentTypes.loginRequired)}>
            {
              submitState.loading ? ts('loading') : ts('send')
            }
          </Button>
        </div>
      </div>
    </form>
  )
}
